import type { MusicAnalytics } from "@/api/__generated__/models";
import { DiffRatioDisplay } from "@/components/dashboard/diff-ratio-display";
import { StackedAreaChart } from "@/components/dashboard/stacked-area-chart";
import { useAnalyticsMonth } from "@/routes/custom-music/dsp-analytics/context/dsp-analytics-context";
import { stackedAreaChartColors } from "@/routes/custom-music/dsp-analytics/utils/color";
import { formatCurrency } from "@/utils/format";
import { Loading, ShadowBox, Skeleton, Typography } from "@backpac/monica";
import { Suspense, useMemo } from "react";

interface RevenueChartProps {
  analytics: MusicAnalytics[];
  previousAnalytics: MusicAnalytics[];
  selectedIds: string[];
}

export const RevenueChart = (props: RevenueChartProps) => {
  return (
    <ShadowBox className="flex flex-col gap-6 p-6">
      <Suspense fallback={<RevenueChart.RevenuePending />}>
        <RevenueChart.Revenue {...props} />
      </Suspense>
    </ShadowBox>
  );
};

RevenueChart.RevenuePending = () => {
  const { formatedMonthRange } = useAnalyticsMonth();
  return (
    <div className="flex flex-col bg-white">
      <div className="flex flex-row justify-between items-center mb-2">
        <Typography size="md" font="unbounded" color="textPrimary">
          Revenue
        </Typography>
        <Typography size="xs" color="textMuted">
          {formatedMonthRange}
        </Typography>
      </div>
      <div className="flex flex-row justify-between items-center mb-9">
        <Skeleton.Base width={120} height={30} />
      </div>
      <div className="flex h-44 items-center justify-center">
        <Loading />
      </div>
    </div>
  );
};

RevenueChart.Revenue = ({ analytics, previousAnalytics, selectedIds }: RevenueChartProps) => {
  const { formatedMonthRange } = useAnalyticsMonth();

  const totalRevenue = useMemo(() => {
    return analytics.filter((item) => selectedIds.includes(item.name)).reduce((acc, curr) => acc + curr.revenue, 0);
  }, [analytics, selectedIds]);

  const previousTotalRevenue = useMemo(() => {
    return previousAnalytics
      .filter((item) => selectedIds.includes(item.name))
      .reduce((acc, curr) => acc + curr.revenue, 0);
  }, [previousAnalytics, selectedIds]);

  const diffRatio = useMemo(() => {
    if (!totalRevenue || !previousTotalRevenue) return 0;

    return totalRevenue === 0 ? 0 : ((totalRevenue - previousTotalRevenue) / Math.abs(previousTotalRevenue)) * 100;
  }, [previousTotalRevenue, totalRevenue]);

  // Aggregate data by month for stacked area chart
  const data = useMemo(() => {
    const dataMap: Record<string, { [key: string]: number }> = {};

    analytics
      .filter((item) => selectedIds.includes(item.name))
      .forEach((item) => {
        item.monthlyData.forEach((monthlyData) => {
          if (!dataMap[monthlyData.settlementMonth]) {
            dataMap[monthlyData.settlementMonth] = {};
          }
          dataMap[monthlyData.settlementMonth][item.name] = monthlyData.revenue;
        });
      });

    return Object.entries(dataMap)
      .sort(([dateA], [dateB]) => dateA.localeCompare(dateB)) // sort by date ascending
      .map(([date, data]) => ({
        date,
        ...data,
      }));
  }, [analytics, selectedIds]);

  const series = useMemo(() => {
    return analytics
      .sort((a, b) => b.revenue - a.revenue) // sort by revenue descending
      .map((item, idx) => ({
        dataKey: item.name,
        name: item.name,
        color: stackedAreaChartColors[idx],
      }));
  }, [analytics]);

  return (
    <div className="flex flex-col bg-white">
      <div className="flex flex-row justify-between items-center mb-2">
        <Typography size="md" font="unbounded" color="textPrimary">
          Revenue
        </Typography>
        <Typography size="xs" color="textMuted">
          {formatedMonthRange}
        </Typography>
      </div>
      <div className="flex flex-row justify-between items-center mb-9">
        <div className="flex gap-3 items-center">
          <Typography size="xl" color="textPrimary" weight={600}>
            {formatCurrency(totalRevenue)}
          </Typography>
          {diffRatio !== 0 && <DiffRatioDisplay diffRatio={diffRatio} />}
        </div>
      </div>
      <div className="flex h-44">
        <StackedAreaChart data={data} series={series} formatValue={(v) => formatCurrency(v)} dateType="month" />
      </div>
    </div>
  );
};
